import { ReactNode, useState } from 'react'
import { toast } from 'react-toastify'

import {
  CubajeDetailsBillingInterface,
  FinancialEconomicCycleReporteInterface,
  Modifier,
  OnlineBillShippingInterface,
  OrdersManagedByInterface,
  OrderSummary,
  PaginateInterface,
  RegisterBillingInterface,
  StockAviableInterface,
} from '../interfaces/ServerInterfaces'
import { useAppDispatch } from '../store/hooks'
import { setDeliverymans } from '../store/slices/nomenclatorSlice'
import { printPriceWithCommasAndPeriods } from '../utils/functions'
import {
  exportExcel,
  formatDateForTable,
  generateUrlParams,
} from '../utils/helpers'
import { translateOrderOrigin, translateOrderState } from '../utils/translate'
import query from './APIServices'
import useServer from './useServerMain'
import { BasicType } from '@/interfaces/InterfacesLocal'

export interface ShippingWhitPickUpInterface
  extends OnlineBillShippingInterface {
  pickUpInStore: boolean
}

// New interfaces for overdue payments
export interface OverduePaymentInterface {
  id: number
  orderId: number
  clientName: string
  amount: number
  codeCurrency: string
  dueDate: string
  daysOverdue: number
  status: string
  createdAt: string
  updatedAt: string
}

// New interfaces for prepaid payments
export interface PrepaidPaymentInterface {
  id: number
  paymentNumber: number
  paymentNumberClient: number
  status: 'PAID' | 'REFUNDED' | 'USED'
  amount: number
  description: string
  codeCurrency: string
  paymentWay: string
  clientId: number
  businessId: number
  orderReceiptId: number | null
  createdAt: string
  updatedAt: string
  client: {
    id: number
    firstName: string
    lastName: string
    email: string
  }
}

// New interface for deliverers
export interface DelivererInterface {
  id: number
  name: string
  phone: string
  email: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// New interface for billing data
export interface BillingDataInterface {
  clientId: number
  name: string
  salesById: number
  registeredAt: string
  paymentDeadlineAt: string
  customerNote: string
  areaSalesId: number
  products: {
    productId: number
    quantity: number
  }[]
  pickUpInStore: boolean
  coupons?: string[]
  discount?: number
  comission?: number
  shippingPrice?: {
    amount: number
    codeCurrency: string
  }
  shipping?: OnlineBillShippingInterface
}

// New interface for pre-billing data
export interface PreBillingDataInterface {
  clientId: number
  name: string
  salesById: number
  registeredAt: string
  paymentDeadlineAt: string
  customerNote: string
  areaSalesId: number
  products: {
    productId: number
    quantity: number
  }[]
  pickUpInStore: boolean
  coupons?: string[]
  discount?: number
  comission?: number
  shippingPrice?: {
    amount: number
    codeCurrency: string
  }
  shipping?: OnlineBillShippingInterface
}

// New interface for prepaid data
export interface PrepaidDataInterface {
  clientId: number
  amount: number
  description: string
  codeCurrency: string
  paymentWay: string
}

// New interface for modifier data
export interface ModifierDataInterface {
  name: string
  type: 'tax' | 'discount'
  amount: number
  codeCurrency: string
  index: number
  active: boolean
  applyToGrossSales: boolean
  applyAcumulative: boolean
  applyFixedAmount: boolean
  showName: string
  observations?: string
  areaId: number
  fixedPrice?: {
    amount: number
    codeCurrency: string
  }
}

// New interface for financial report data
export interface FinancialReportDataInterface {
  dateFrom: string
  dateTo: string
  areaId?: number
  economicCycleId?: number
}

// New interface for finished products disponibility data
export interface FinishedProductsDisponibilityDataInterface {
  listStockIds?: number[]
  preBillingMatch?: boolean
  deliveryAt?: string
}

// New interface for accounting summary data
export interface AccountingSummaryDataInterface {
  dateFrom: string
  dateTo: string
  areaId?: number
  economicCycleId?: number
}

// New interface for transform order data
export interface TransformOrderDataInterface {
  operationNumber: number
  registeredAt: string
  paymentDeadlineAt?: string
  customerNote?: string
}

// New interface for billing details data
export interface BillingDetailsDataInterface {
  name?: string
  customerNote?: string
  paymentDeadlineAt?: string
  shipping?: OnlineBillShippingInterface
}

// New interface for export data object
export interface ExportDataObjectInterface {
  Tipo: string
  'No.': string
  Nombre: string
  Cliente: string
  Estado: string
  Emisión: string
  Origen: string
  [key: string]: string | number
}

export interface SummaryCarriersInterface {
  totals: {
    shipments: number
    km: number
    shippingPrice: {
      CUP: number
    }
    commissions: {
      CUP: number
    }
  }
  transporters: [
    {
      transporter: string
      email: string
      shipments: number
      totalKms: number
      shippingPrice: [
        {
          amount: number
          codeCurrency: string
        },
      ]
      commissions: [
        {
          amount: number
          codeCurrency: string
        },
      ]
      details: [
        {
          shippingVehicle: {
            id: number
            name: string
            type: string
          }
          destination: string
          orderId: number
          deliveryDate: string
          totalKms: number
          shippingPrice: {
            amount: number
            codeCurrency: string
          }
          commission: {
            amount: number
            codeCurrency: string
          }
        },
      ]
    },
  ]
}

export const useServerBilling = () => {
  //=> Hooks
  const { manageErrors } = useServer()

  //=> States
  const [modalDetail, setModalDetail] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isFetchingAux, setIsFetchingAux] = useState<boolean>(true)
  const [prepaidWizardModal, setPrepaidWizardModal] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const [deliverers, setDeliverers] = useState<DelivererInterface[]>([])
  const [overduePaginate, setOverduePaginate] = useState<PaginateInterface>({
    currentPage: 0,
    totalItems: 0,
    totalPages: 0,
  })

  const [registerPaginate, setRegisterPaginate] = useState<PaginateInterface>({
    currentPage: 0,
    totalItems: 0,
    totalPages: 0,
  })

  const [prepaidPaginate, setPrepaidPaginate] = useState<PaginateInterface>({
    currentPage: 0,
    totalItems: 0,
    totalPages: 0,
  })
  const [modifierPaginate, setModifierPaginate] = useState<PaginateInterface>({
    currentPage: 0,
    totalItems: 0,
    totalPages: 0,
  })

  // Registros
  const [registerBillingList, setRegisterBillingList] = useState<
    RegisterBillingInterface[]
  >([])
  const [cubajeDetails, setCubajeDetails] =
    useState<CubajeDetailsBillingInterface>()
  // Cobros Vencidos
  const [overduePaymentsList, setOverduePaymentsList] = useState<
    OverduePaymentInterface[]
  >([])
  //  Pagos anticipados
  const [prepaidsList, setPrepaidsList] = useState<PrepaidPaymentInterface[]>(
    []
  )
  // todo: tipar prepaidPayment
  const [prepaidById, setPrepaidById] =
    useState<PrepaidPaymentInterface | null>(null)
  //  Orden por Id
  const [orderById, setorderById] = useState<RegisterBillingInterface | null>()
  //
  const [AllSummaryOrders, setAllSummaryOrders] = useState<OrderSummary>()
  // Descuetno y comisiones
  const [allModifier, setAllModifier] = useState<Modifier[]>([])
  // List financial report in economic cycle
  const [financialEconomicCycleReporte, setFinancialEconomicCycleReporte] =
    useState<FinancialEconomicCycleReporteInterface>()
  // Report Orders By Managed
  const [reportOrdersManagedBy, setReportOrdersManagedBy] =
    useState<OrdersManagedByInterface>()
  // Stock Availability Report
  const [stockAvailabilityReport, setStockAvailabilityReport] =
    useState<StockAviableInterface[]>()
  // Resumen contable por órdenes
  const [accountingSummaryByOrders, setAccountingSummaryByOrders] =
    useState<OrdersManagedByInterface>()
  // Resumen contable de envios por transportistas
  const [salesSummaryCarriers, setSalesSummaryCarriers] =
    useState<SummaryCarriersInterface | null>(null)

  //  Fetch Methods
  const getAllOverduePayments = async (
    filter: Record<string, string | number | boolean | null>
  ): Promise<void> => {
    setIsFetching(true)
    await query
      .get(`/administration/overdue-payment${generateUrlParams(filter)}`)
      .then((res) => {
        setOverduePaymentsList(res.data.items)
        setOverduePaginate({
          currentPage: res.data.currentPage,
          totalItems: res.data.totalItems,
          totalPages: res.data.totalPages,
        })
      })
      .catch((error) => manageErrors(error))

    setIsFetching(false)
  }
  const getAllOverduePaymentsV2 = async (
    filter: Record<string, string | number | boolean | null>
  ): Promise<void> => {
    setIsFetching(true)
    await query
      .get(`/administration/overdue-paymentV2${generateUrlParams(filter)}`)
      .then((res) => {
        setOverduePaymentsList(res.data.items)
        setOverduePaginate({
          currentPage: res.data.currentPage,
          totalItems: res.data.totalItems,
          totalPages: res.data.totalPages,
        })
      })
      .catch((error) => manageErrors(error))

    setIsFetching(false)
  }
  const getAllDeliverers = async (): Promise<void> => {
    await query
      .get(`/shipping/deliverers`)
      .then((res) => {
        setDeliverers(res.data.items)
        dispatch(setDeliverymans(res.data.items))
      })
      .catch((error) => manageErrors(error))
  }

  const getAllRegisterBillingList = async (
    filter: Record<string, string | number | boolean | null>
  ) => {
    setIsFetching(true)
    await query
      .get(`/administration/billing-order${generateUrlParams(filter)}`)
      .then((res) => {
        setRegisterBillingList(res.data.items)
        setRegisterPaginate({
          currentPage: res.data.currentPage,
          totalItems: res.data.totalItems,
          totalPages: res.data.totalPages,
        })
        return res.data.items
      })
      .catch((error) => manageErrors(error))

    setIsFetching(false)
  }

  const exportExcelOrders = async (
    filter: Record<string, string | number | boolean | null>,
    fileName: string,
    callBack?: () => void
  ) => {
    setIsLoading(true)
    await query
      .get(`/administration/billing-order${generateUrlParams(filter)}`)
      .then((res) => {
        const data: RegisterBillingInterface[] = res.data.items
        const dataToExport: ExportDataObjectInterface[] = []
        data?.forEach((item) => {
          // Primero crea el objeto con los campos iniciales
          const dataObject: ExportDataObjectInterface = {
            Tipo: !item?.isPreReceipt ? 'Factura' : 'Pre-Factura',
            'No.': !item?.isPreReceipt
              ? item.operationNumber.toString()
              : item.preOperationNumber?.toString() || '',
            Nombre: item.name ?? '',
            Cliente: `${item.client?.firstName ?? ''} ${
              item.client?.lastName ?? ''
            }`,
            Estado: translateOrderState(item.status),
            Emisión: formatDateForTable(item?.createdAt),
            Origen: translateOrderOrigin(item.origin),
          }

          // Añadir dinámicamente los precios de importe
          item.totalToPay.forEach((total) => {
            dataObject[`Importe en ${total.codeCurrency}`] =
              printPriceWithCommasAndPeriods(total.amount)
          })

          dataToExport.push(dataObject)
        })

        exportExcel(dataToExport, fileName).then(() => {
          if (callBack) callBack()
        })
      })
      .catch((error) => manageErrors(error))

    setIsLoading(false)
  }

  const updateOrderListLocally = (order: RegisterBillingInterface) => {
    const allOrders = [...registerBillingList]
    const idx = registerBillingList.findIndex((item) => item.id === order.id)
    allOrders.splice(idx, 1, { ...allOrders[idx], ...order })
    setRegisterBillingList(allOrders)
  }

  const getAllPrepaidsList = async (
    filter: Record<string, string | number | boolean | null>
  ): Promise<void> => {
    setIsLoading(true)
    await query
      .get(`/administration/prepaid-payments${generateUrlParams(filter)}`)
      .then((res) => {
        setPrepaidsList(res.data.items)
        setPrepaidPaginate({
          currentPage: res.data.currentPage,
          totalItems: res.data.totalItems,
          totalPages: res.data.totalPages,
        })
      })
      .catch((error) => manageErrors(error))

    setIsLoading(false)
  }

  const getPrepaidPaymentById = async (
    id: number,
    filter?: Record<string, string | number | boolean | null>
  ): Promise<void> => {
    setIsFetching(true)
    await query
      .get(`/administration/prepaid-payments/${id}${generateUrlParams(filter)}`)
      .then((res) => {
        setPrepaidById(res.data)
      })
      .catch((error) => manageErrors(error))

    setIsFetching(false)
  }

  const cancelOrder = async (id: number): Promise<void> => {
    setIsFetching(true)
    await query
      // .deleteAPI(`/order/cancel/${id}`, {})
      .deleteAPI(`/administration/billing-order/${id}`, {})
      .then((res) => {
        const update = registerBillingList.map((item) => {
          if (item.id === id) {
            return res.data
          }
          return item
        })
        setRegisterBillingList(update)

        toast.success('Se canceló la orden correctamente')
      })
      .catch((error) => manageErrors(error))
    setIsFetching(false)
  }

  const sendRemaindRegister = async (id: number) => {
    setIsLoading(true)
    await query
      .post(`/administration/remind-orders`, { orderId: id })
      .then(() => {
        toast.success('Recordatorio enviado correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const sendPredispatchSelected = async (
    areaSalesId: number,
    listIdsPreBilling: number[],
    comp: (_amountDispatches: number, _amountOrdersMove: number) => ReactNode,
    onSuccess?: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/billing-order/predispatch`, {
        areaSalesId,
        listIdsPreBilling,
      })
      .then((res) => {
        toast.success(
          comp(res.data.amountDispatches, res.data.amountOrdersMove)
        )
        if (onSuccess) onSuccess()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const sendCalculateCubaje = async (
    listOrderIds: number[],
    onSuccess?: () => void
  ) => {
    setIsLoading(true)
    await query
      .post(`/administration/billing-order/calculate-cubaje`, {
        listOrderIds,
      })
      .then((res) => {
        setCubajeDetails(res.data)
        if (onSuccess) onSuccess()
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const refundBillingOrder = async (id: number) => {
    setIsFetching(true)
    await query
      .post(`/administration/refund-order/${id}`, {})
      .then(() => {
        setRegisterBillingList((prevRegisters) =>
          prevRegisters.map((item) =>
            item.id === id ? { ...item, status: 'REFUNDED' } : item
          )
        )

        toast.success('Reembolso realizado correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const refundPrepaidPayment = async (
    id: number,
    callback: (_data: PrepaidPaymentInterface) => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/refund-prepaidPayment/${id}`, {})
      .then((res) => {
        const update = prepaidsList.map((item: PrepaidPaymentInterface) => {
          if (item.id === id) {
            return res.data
          }
          return item
        })
        //setModalDetail(null);
        setPrepaidsList(update)
        callback(res.data)
        toast.success('Reembolso realizado correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const getResume = async (): Promise<void> => {
    setIsFetching(true)
    query
      .get(`/billings/resume`)
      .then((res) => {
        setOverduePaymentsList(res.data)
      })
      .catch((error) => manageErrors(error))

    setIsFetching(false)
  }

  const addNewBilling = async (
    newBilling: BillingDataInterface,
    callback: (_id: number) => void,
    finallyCase?: () => void
  ): Promise<void> => {
    setIsLoading(true)
    query
      .post('/administration/billing-order', newBilling)
      .then((resp) => {
        setRegisterBillingList([resp.data, ...registerBillingList])

        setRegisterPaginate({
          ...registerPaginate,
          totalItems: registerPaginate?.totalItems + 1,
        })
        //----- set del estado para el modal de pago --->
        callback(resp.data.id)
        setorderById(resp.data)
        toast.success('Factura creada exitosamente')
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        if (finallyCase) finallyCase()
      })
    setIsLoading(false)
  }

  const addNewPreBilling = async (
    newPreBilling: PreBillingDataInterface,
    callback: () => void,
    finallyCase?: () => void
  ): Promise<void> => {
    setIsLoading(true)
    query
      .post('/administration/pre-billing-order', newPreBilling)
      .then((resp) => {
        setRegisterBillingList([resp.data, ...registerBillingList])
        setRegisterPaginate({
          ...registerPaginate,
          totalItems: registerPaginate?.totalItems + 1,
        })

        callback()
        toast.success('Pre-factura creada exitosamente')
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        if (finallyCase) finallyCase()
      })
    setIsLoading(false)
  }
  const editBilling = async (
    id: number,
    data: BillingDataInterface,
    callBack?: (_data: RegisterBillingInterface) => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/billing-order/${id}`, data)
      .then((resp) => {
        const update = registerBillingList?.map((item) => {
          if (item.id === resp.data.id) {
            return resp.data
          }
          return item
        })
        setorderById(resp.data)
        setRegisterBillingList(update)
        if (callBack) callBack(resp.data)
        toast.success(`Edición realizada exitosamente`)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  const editBillingDetails = async (
    id: number,
    data: BillingDetailsDataInterface,
    callBack?: (_data: RegisterBillingInterface) => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/billing-order/${id}/details`, data)
      .then((resp) => {
        const update = registerBillingList?.map((item) => {
          if (item.id === resp.data.id) {
            return resp.data
          }
          return item
        })
        setorderById((prev) => ({
          ...resp.data,
          partialPayments: prev?.partialPayments,
          records: prev?.records,
        }))
        setRegisterBillingList(update)
        if (callBack) callBack(resp.data)
        toast.success(`Edición realizada exitosamente`)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const addNewPrepaid = async (
    newPrepaid: PrepaidDataInterface,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .post('/administration/prepaid-payments', newPrepaid)
      .then((resp) => {
        setPrepaidsList([resp.data, ...prepaidsList])
        setPrepaidPaginate({
          ...prepaidPaginate,
          totalItems: registerPaginate?.totalItems + 1,
        })
        callback()
        toast.success('Pago anticipado creado exitosamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  const editPrepaid = async (
    id: number,
    data: PrepaidDataInterface,
    callback: (_data: PrepaidPaymentInterface) => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/prepaid-payments/${id}`, data)
      .then((resp) => {
        callback(resp.data)
        toast.success('Pago anticipado editado exitosamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const GetAllSummaryOrders = async () => {
    setIsFetching(true)
    query
      .get(`/administration/summary-orders`)
      .then((res) => {
        setAllSummaryOrders(res.data)
      })
      .catch((error) => manageErrors(error))

    setIsFetching(false)
  }
  const getOrderBillingById = async (id: number | string) => {
    setIsLoading(true)
    setIsFetchingAux(true)
    await query
      .get(`/administration/billing-order/${id}`)
      .then((resp) => {
        setorderById(resp.data)
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const convertPreBillToBill = async (
    id: number,
    data: TransformOrderDataInterface
  ) => {
    setIsLoading(true)
    await query
      .patch(`/administration/transform-orders/${id}`, data)
      .then((resp) => {
        setRegisterBillingList((prevRegisters) =>
          prevRegisters.map((item) => (item.id === id ? resp.data : item))
        )

        toast.success('Factura creada exitosamente')
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const deletePartialPayment = async (id: number, callback: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/partialPayment/${id}`, {})
      .then((response) => {
        setorderById(response.data)
        callback()
        toast.success('Pago eliminado correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const updateSingleOrderState = (order: RegisterBillingInterface) => {
    setorderById(order)
  }

  const getAllModifier = async (
    filter: Record<string, string | number | boolean | null>
  ): Promise<void> => {
    setIsLoading(true)
    await query
      .get(`/administration/modifier${generateUrlParams(filter)}`)
      .then((res) => {
        setAllModifier(res.data.items)
        setModifierPaginate({
          currentPage: res.data.currentPage,
          totalItems: res.data.totalItems,
          totalPages: res.data.totalPages,
        })
      })
      .catch((error) => manageErrors(error))

    setIsLoading(false)
  }
  const addNewModifier = async (
    data: ModifierDataInterface,
    callback: () => void,
    finallyCase?: () => void
  ): Promise<void> => {
    setIsLoading(true)
    query
      .post('/administration/modifier', data)
      .then((resp) => {
        setAllModifier([resp.data, ...allModifier])
        setModifierPaginate({
          ...modifierPaginate,
          totalItems: modifierPaginate?.totalItems + 1,
        })

        callback()
        toast.success('Modificador agregado')
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        if (finallyCase) finallyCase()
      })
    setIsLoading(false)
  }

  const deleteModifier = async (id: number, callback: () => void) => {
    setIsLoading(true)
    await query
      .deleteAPI(`/administration/modifier/${id}`, {})
      .then(() => {
        setAllModifier((item) => item.filter((item) => item.id !== id))
        setModifierPaginate({
          ...modifierPaginate,
          totalItems: modifierPaginate?.totalItems - 1,
        })
        callback()
        toast.success('Modificador eliminado')
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }
  const updateModifier = async (
    id: number,
    data: Partial<ModifierDataInterface>,
    callback: () => void,
    finallyCase?: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/modifier/${id}`, data)
      .then((resp) => {
        const update = allModifier.map((item) => {
          if (item.id === id) {
            return resp.data
          }
          return item
        })
        setAllModifier(update)
        toast.success('Modificador actualizado')
        callback()
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        if (finallyCase) finallyCase()
      })
    setIsFetching(false)
  }

  const getFinancialEconomicCycleReporte = async (
    _data: FinancialReportDataInterface
  ): Promise<void> => {
    setIsFetching(true)
    query
      .post('/report/billing/list/financial', _data)
      .then((resp) => {
        setFinancialEconomicCycleReporte(resp.data)
        /*  toast.success("Reporte listo"); */
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const getReportOrdersManagedBy = async (
    dateFrom: string,
    dateTo: string,
    callback: (_data: OrdersManagedByInterface) => void
  ): Promise<void> => {
    setIsFetching(true)
    query
      .get(`/report/orders/ByCommercial?dateFrom=${dateFrom}&dateTo=${dateTo}`)
      .then((resp) => {
        setReportOrdersManagedBy(resp.data)
        callback(resp.data)
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const getReportFinishedProductsDisponibility = async (
    data: FinishedProductsDisponibilityDataInterface,
    callback: (_data: StockAviableInterface[]) => void
  ): Promise<void> => {
    setIsFetching(true)
    query
      .post(`/report/product/readytosale-disponibility`, data)
      .then((resp) => {
        setStockAvailabilityReport(resp.data)
        callback(resp.data)
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const getAccountingSummaryByOrders = async (
    data: AccountingSummaryDataInterface,
    callback?: (_data: OrdersManagedByInterface) => void
  ): Promise<void> => {
    setIsFetching(true)
    query
      .get(
        `/report/orders/summary${generateUrlParams(
          data as unknown as Record<string, string | number | boolean | null>
        )}`
      )
      .then((resp) => {
        setAccountingSummaryByOrders(resp.data)
        if (callback) callback(resp.data)
      })
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const getAllSalesSummaryByCarriers = async (filter?: BasicType) => {
    setIsLoading(true)
    await query
      .get(`/report/orders/transporter-commissions${generateUrlParams(filter)}`)
      .then((resp) => {
        setSalesSummaryCarriers(resp.data)
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const FindAllCashOperations = async (
    _economicCycleId: number
  ): Promise<void> => {
    setIsFetching(true)
    query
      .get(`/sales/cash-operation?economicCycleId=${_economicCycleId}`)
      .catch((e) => {
        manageErrors(e)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const TransformPreReceiptIntoReceipt = async (
    preReceiptId: number,
    data: Record<string, string | number | boolean | null>,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/transform-orders/${preReceiptId}`, data)
      .then((resp) => {
        toast.success('Convertido a factura satisfactoriamente')

        setRegisterBillingList(
          registerBillingList.map((order) => {
            if (order.id === preReceiptId) {
              return resp.data
            } else {
              return order
            }
          })
        )
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  return {
    isFetching,
    isLoading,
    registerBillingList,
    overduePaymentsList,
    exportExcelOrders,
    getAllRegisterBillingList,
    getAllOverduePayments,
    getResume,
    addNewBilling,
    overduePaginate,
    registerPaginate,
    GetAllSummaryOrders,
    AllSummaryOrders,
    cancelOrder,
    sendRemaindRegister,
    refundBillingOrder,
    addNewPreBilling,
    prepaidPaginate,
    getAllPrepaidsList,
    prepaidsList,
    addNewPrepaid,
    getOrderBillingById,
    orderById,
    TransformPreReceiptIntoReceipt,
    editBilling,
    editBillingDetails,
    convertPreBillToBill,
    getPrepaidPaymentById,
    prepaidById,
    refundPrepaidPayment,
    modalDetail,
    setModalDetail,
    setPrepaidWizardModal,
    prepaidWizardModal,
    setPrepaidsList,
    deletePartialPayment,
    updateSingleOrderState,
    updateOrderListLocally,
    getAllOverduePaymentsV2,
    getReportFinishedProductsDisponibility,
    editPrepaid,
    getAllModifier,
    addNewModifier,
    deleteModifier,
    updateModifier,
    allModifier,
    modifierPaginate,
    isFetchingAux,
    setIsLoading,
    deliverers,
    getAllDeliverers,

    getFinancialEconomicCycleReporte,
    financialEconomicCycleReporte,

    getReportOrdersManagedBy,
    reportOrdersManagedBy,

    stockAvailabilityReport,

    getAccountingSummaryByOrders,
    accountingSummaryByOrders,
    FindAllCashOperations,
    sendPredispatchSelected,
    sendCalculateCubaje,
    cubajeDetails,

    getAllSalesSummaryByCarriers,
    salesSummaryCarriers,
  }
}
